import React, { useEffect, useState, useCallback } from 'react'
import moment from 'moment'
import { useInterval } from 'react-use'

import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import { DateTimePicker } from '@material-ui/pickers'

import { withAdmin } from '../../services/msal'

import useFilterParams from '../../hooks/useFilterParams'

import StreamHealth from './StreamHealth'
import ServiceHealth from './ServiceHealth'
import MediaServerHealth from './MediaServerHealth'
import NetworkHealth from './NetworkHealth'


const UPDATE_SECONDS =
  Number(window.__ENV.REACT_APP_MONITOR_PAGE_UPDATE_SECONDS ?? null) || 60

const periodToDateTimeRange = (period) => {
  if (period === 'custom') return {}
  return {
    from: period === 'hour'
      ? moment().subtract(1, 'hour')
      : period === '6hour'
        ? moment().subtract(6, 'hour')
        : period === '24hour'
          ? moment().subtract(24, 'hour')
          : period === 'week'
            ? moment().subtract(7, 'day')
            // default to 24 hours
            : moment().subtract(24, 'hour'),
    until: moment(),
  }
}

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  formControl: {
    minWidth: 150,
    marginRight: theme.spacing(1),
  },
}))

const Monitor = () => {
  const classes = useStyles()

  const [
    {
      monitor_period: _monitorPeriod,
      from: urlFrom,
      until: urlUntil,
    },
    setFilterParams,
  ] = useFilterParams()

  const [
    {
      from: stateFrom,
      until: stateUntil,
    },
    setFilterState,
  ] = useFilterParams({
    urlKey: useState(),
    ...periodToDateTimeRange(_monitorPeriod),
  })

  const monitorPeriod = (!_monitorPeriod && (urlFrom || urlUntil) ? 'custom' : _monitorPeriod) ?? '24hour'
  const from = monitorPeriod === 'custom' ? urlFrom : stateFrom
  const until = monitorPeriod === 'custom' ? urlUntil : stateUntil

  const setFrom = useCallback((from) => setFilterParams({
    from,
    monitor_period: undefined,
  }), [setFilterParams])

  const setUntil = useCallback((until) => setFilterParams({
    until,
    monitor_period: undefined,
  }), [setFilterParams])

  const setMonitorPeriod = useCallback((monitorPeriod) => {
    if (monitorPeriod === 'custom') {
      setFilterParams({
        monitor_period: undefined,
        from: stateFrom,
        until: stateUntil,
      })
      return
    }

    setFilterParams({
      monitor_period: monitorPeriod === '24hour' ? undefined : monitorPeriod,
      from: undefined,
      until: undefined,
    })

    setFilterState(periodToDateTimeRange(monitorPeriod))
  }, [
    setFilterParams,
    setFilterState,
    stateFrom,
    stateUntil,
  ])

  useInterval(
    () => setMonitorPeriod(monitorPeriod),
    monitorPeriod === 'custom' ? null : UPDATE_SECONDS * 1000
  )

  useEffect(() => {
    if (until.isSameOrBefore(from, 'second')) {
      // go back a full day if "until" is the same as "from"
      setFrom(until.clone().subtract(24, 'hour'))
    }
  }, [from, until, setFrom])

  if (until.isSameOrBefore(from, 'second')) {
    return null
  }

  return (
    <>
      <Box p={3}>
        <FormControl className={classes.formControl} margin="dense">
          <InputLabel id="monitor-select-label" margin="dense">
            Monitor period
          </InputLabel>
          <Select
            labelId="monitor-select-label"
            id="monitor-select"
            value={monitorPeriod}
            onChange={(e) => setMonitorPeriod(e.target.value)}
            label="Monitor period"
            margin="dense"
          >
            {/*
            Disable the options below for now.
            See ado-6451-remove-1-and-6-hours-from-metrics-date-picker
            <MenuItem value="hour">Last hour</MenuItem>
            <MenuItem value="6hour">Last 6 hours</MenuItem>
            */}
            <MenuItem value="24hour">Last 24 hours</MenuItem>
            <MenuItem value="week">Last week</MenuItem>
            <MenuItem value="custom"><em>Custom</em></MenuItem>
          </Select>
        </FormControl>
        <DateTimePicker
          className={classes.formControl}
          label="From"
          format="DD MMM YYYY HH:mm"
          onChange={setFrom}
          value={from}
          maxDate={moment()}
          margin="dense"
          disabled={monitorPeriod !== 'custom'}
        />
        <DateTimePicker
          className={classes.formControl}
          label="Until"
          format="DD MMM YYYY HH:mm"
          onChange={setUntil}
          value={until}
          maxDate={moment()}
          margin="dense"
          disabled={monitorPeriod !== 'custom'}
        />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={5}><StreamHealth from={from} until={until} /></Grid>
        <Grid item xs={12} sm={6} md={5}><MediaServerHealth from={from} until={until} /></Grid>
        <Grid item xs={12} sm={6} md={2}><ServiceHealth from={from} until={until} /></Grid>
        <Grid item xs={12}><NetworkHealth from={from} until={until} /></Grid>
      </Grid>
    </>
  )
}

export const MonitorPage = withAdmin(Monitor)
